import type { AppLogger, ServerLoggerConfig } from './types'

const DEFAULT_ALLOWED_KEYS = ['userId', 'pictureId', 'endpoint', 'feature', 'context', 'email']

/**
 * Creates a server-side Winston logger with optional Sentry transport.
 *
 * Usage:
 * ```ts
 * import * as winston from 'winston'
 * import Transport from 'winston-transport'
 * import * as Sentry from '@sentry/node'
 *
 * export const logger = createServerLogger({
 *     isProd: serverConfig.IS_PROD,
 *     sentryEnabled: serverConfig.SENTRY_ENABLED,
 *     winston: { createLogger: winston.createLogger, format: winston.format, transports: winston.transports },
 *     winstonTransport: Transport,
 *     sentry: Sentry,
 * })
 * ```
 */
export function createServerLogger(config: ServerLoggerConfig): AppLogger {
    const { isProd, sentryEnabled, winston, winstonTransport, sentry } = config
    const allowedKeys = config.allowedKeys ?? DEFAULT_ALLOWED_KEYS
    const { createLogger, format, transports } = winston
    const { combine, timestamp, printf, colorize, errors } = format

    const toSafeExtraFormat = format((info: any) => {
        const safeExtra: Record<string, unknown> = {}

        for (const key of allowedKeys) {
            if (info[key] !== undefined) {
                safeExtra[key] = info[key]
                delete info[key]
            }
        }

        info.extra = {
            ...(typeof info.extra === 'object' && info.extra !== null ? info.extra : {}),
            ...safeExtra,
        }

        return info
    })

    const logFormat = printf(({ level, message, timestamp, stack }: any) => {
        return `${timestamp} ${level}: ${stack || message}`
    })

    const emojiLogFormat = printf(({ level, message, timestamp, stack }: any) => {
        // eslint-disable-next-line no-control-regex
        const cleanLevel = level.replace(/\x1B\[\d{1,2}m/g, '')
        const base = `${timestamp} ${cleanLevel}: ${stack || message}`
        if (cleanLevel === 'error') return `❌ ${base}`
        if (cleanLevel === 'warn') return `⚠️  ${base}`
        if (cleanLevel === 'info') return `ℹ️  ${base}`
        if (cleanLevel === 'debug') return `🐛 ${base}`

        return base
    })

    const isDev = !isProd

    const loggerTransports: any[] = [new transports.Console()]

    if (sentryEnabled && sentry) {
        const SentryWinstonTransport = sentry.createSentryWinstonTransport(winstonTransport, {
            levels: ['info', 'error', 'warn'],
        })
        loggerTransports.push(new SentryWinstonTransport())
    }

    const loggerInstance = createLogger({
        level: isDev ? 'debug' : 'info',
        format: combine(
            toSafeExtraFormat(),
            colorize(),
            timestamp({ format: 'YYYY-MM-DD HH:mm:ss' }),
            errors({ stack: true }),
            isDev ? emojiLogFormat : logFormat,
        ),
        transports: loggerTransports,
    })

    return {
        error: (message: string, error?: unknown, context: Record<string, unknown> = {}) => {
            loggerInstance.error(message, {
                ...context,
                stack: error instanceof Error ? error.stack : undefined,
                originalError: error,
            })
        },

        warn: (message: string, context: Record<string, unknown> = {}) => {
            loggerInstance.warn(message, context)
        },

        info: (message: string, context: Record<string, unknown> = {}) => {
            loggerInstance.info(message, context)
        },

        debug: (message: string, context: Record<string, unknown> = {}) => {
            loggerInstance.debug(message, context)
        },
    }
}
